import { enqueueSnackbar } from 'notistack'

import { notifyError } from '@/utils/notifyError'

import { ApiError } from '../ApiError'

export type BulkOperationFailure<TItem> = {
  /** The item that failed to process */
  item: TItem
  /** The error that occurred during processing */
  error: unknown
  /** The index of the item in the original array */
  index: number
}

export function extractErrorMessage(error: unknown): string {
  if (error instanceof ApiError) {
    return error.message
  }

  if (error instanceof Error) {
    return error.message
  }

  if (typeof error === 'string') {
    return error
  }

  if (error && typeof error === 'object' && 'message' in error) {
    return String((error as { message: unknown }).message)
  }

  return 'An unknown error occurred'
}

type RateLimitConfig = {
  /** Maximum number of requests per second (default: 10) */
  requestsPerSecond?: number
  /** Maximum number of concurrent requests in a batch (default: 5) */
  batchSize?: number
}

type BulkOperationOptions<TItem, TResult = void> = {
  /** Array of items to process */
  items: TItem[]
  /** Function that performs the operation on a single item */
  operation: (item: TItem) => Promise<TResult>
  /** Function to refresh data after operations complete */
  refreshData: () => Promise<void> | void
  /** Name of the bulk operation (e.g., "Delete User") for automatic message generation */
  name: string
  /** Optional function to render item display name for error messages */
  getItemDisplayName?: (item: TItem, index: number) => string
  /** Custom error message overrides */
  errorMessage?: {
    /** Message when all operations fail */
    allFailed?: string
    /** Message when some operations fail */
    partialFailure?: string
  }
  /** Rate limiting configuration to prevent API rate limiting */
  rateLimitConfig?: RateLimitConfig
}

function delay(ms: number): Promise<void> {
  return new Promise((resolve) => setTimeout(resolve, ms))
}

async function processBatchedOperations<TItem, TResult>(
  items: TItem[],
  operation: (item: TItem) => Promise<TResult>,
  rateLimitConfig: Required<RateLimitConfig>
): Promise<{
  successCount: number
  failures: BulkOperationFailure<TItem>[]
}> {
  const { requestsPerSecond, batchSize } = rateLimitConfig
  const delayBetweenBatches = Math.ceil(1000 / requestsPerSecond) * batchSize // ms

  const failures: BulkOperationFailure<TItem>[] = []
  let successCount = 0

  for (let i = 0; i < items.length; i += batchSize) {
    const batch = items.slice(i, i + batchSize)

    const batchResults = await Promise.allSettled(batch.map((item) => operation(item)))

    batchResults.forEach((result, batchIndex) => {
      const originalIndex = i + batchIndex
      if (result.status === 'rejected') {
        failures.push({
          item: items[originalIndex],
          error: result.reason,
          index: originalIndex
        })
      } else {
        successCount++
      }
    })

    if (i + batchSize < items.length) {
      await delay(delayBetweenBatches)
    }
  }

  return { successCount, failures }
}

export async function executeBulkOperation<TItem, TResult = void>({
  items,
  operation,
  refreshData,
  name,
  getItemDisplayName = (_item, index) => `Item ${index + 1}`,
  errorMessage,
  rateLimitConfig = {}
}: BulkOperationOptions<TItem, TResult>): Promise<void> {
  if (items.length === 0) {
    return
  }

  // 10 req/sec, batch size 5 to stay under 15 req/sec limit. See Slack 🧵: https://imgarena.slack.com/archives/C065WDSKB2Q/p1756995630098819
  const finalRateLimitConfig: Required<RateLimitConfig> = {
    requestsPerSecond: 10,
    batchSize: 5,
    ...rateLimitConfig
  }

  const { successCount, failures } = await processBatchedOperations(
    items,
    operation,
    finalRateLimitConfig
  )

  const failureCount = failures.length

  if (failureCount === 0) {
    await refreshData()
    return
  }

  if (successCount > 0) {
    await refreshData()

    enqueueSnackbar(`${name}: ${successCount} successful`, { variant: 'success' })
  }

  let message: string
  const errorDetails = failures.map(
    (f) => `${getItemDisplayName(f.item, f.index)}: ${extractErrorMessage(f.error)}`
  )

  if (failureCount === items.length) {
    message =
      errorMessage?.allFailed ||
      `Failed to ${name.toLowerCase()}. All ${items.length} items failed.`
  } else {
    const defaultMessage = `${name}: ${failureCount}/${items.length} failed`
    message = errorMessage?.partialFailure || defaultMessage
  }

  notifyError(message, {
    title: `${name} Failure`,
    details: errorDetails
  })

  throw new Error(message)
}

export function createBulkHandler<TItem, TResult = void>(
  config: Omit<BulkOperationOptions<TItem, TResult>, 'items'>
) {
  return async (items: TItem[]) => {
    await executeBulkOperation({
      items,
      ...config
    })
  }
}
